import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Trash2, Plus, Eye, EyeOff, Star, BookOpen, CheckCircle, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// COMPLETE eBook Table of Contents - organized by section
const REFERENCE_MATERIALS = [
  { page: 1, title: 'Title Page & Purpose of NVC' },
  { page: 5, title: 'Feelings & Needs Exercise' },
  { page: 6, title: 'Feelings List' },
  { page: 7, title: 'Body Sensations' },
  { page: 8, title: 'Your Presence is the Most Precious Gift' },
  { page: 9, title: 'The Golden Rule' },
  { page: 10, title: 'The NVC Tree of Life' },
  { page: 11, title: 'NVC Dance Floors (one person)' },
  { page: 12, title: 'NVC Dance Floors (two people)' },
  { page: 13, title: 'A Compassionate Communication Dialog' },
  { page: 14, title: 'Five Key Practices for a Compassionate Conversation' },
  { page: 15, title: 'Basic Feelings and Needs' },
  { page: 16, title: 'Feelings & Needs Worksheet (Interactive Table)' },
  { page: 17, title: 'NVC: Statements and Questions (OFNR)' },
  { page: 18, title: 'NVC OFNR Practice Sheets (p.18-24)' },
  { page: 25, title: 'Compassionate Communication Wheel' },
  { page: 26, title: 'Worksheet for Finding Mutual Strategies' },
  { page: 27, title: 'Non-feeling Words' },
  { page: 29, title: 'Working with Conflict using NVC (Short)' },
  { page: 31, title: 'Working with Conflict using NVC (Long, p.31-36)' },
  { page: 37, title: 'Compassionate Communication Checklist' },
  { page: 38, title: 'Gratitude Exercises' },
  { page: 40, title: 'Gratitude and Celebration Practices (NVC Style)' },
  { page: 43, title: 'Giving and Receiving Feedback NVC Style' },
  { page: 45, title: 'Connection Roadmap' },
  { page: 46, title: 'Intimacy Building Questions' },
  { page: 48, title: 'The Three Areas of Attention' },
  { page: 49, title: 'The Interpersonal Dance of The 3 Areas' },
  { page: 51, title: 'Our Internal Map of Reality' },
  { page: 52, title: 'I Made It Mean... Worksheet' },
  { page: 53, title: 'Our Quadrune Brain' },
  { page: 54, title: 'The Quadrune Brain and Evolutionary Psychology' },
  { page: 55, title: 'Take In the Good! (Positive Experiences)' },
  { page: 57, title: 'The Fear of Feeling Our Feelings' },
  { page: 59, title: 'Risk the dreaded experience!' },
  { page: 61, title: 'Key Ideas and Assumptions of NVC' },
  { page: 63, title: 'Nonviolent Communication Primer' },
  { page: 64, title: 'The Components of NVC' },
  { page: 65, title: '1. Observations' },
  { page: 66, title: '2. Feelings' },
  { page: 67, title: '3. Needs' },
  { page: 68, title: '4. Requests' },
  { page: 69, title: 'Empathy' },
  { page: 70, title: 'Self-Empathy' },
  { page: 71, title: 'Compassionate Communication Suggestions' },
  { page: 72, title: 'COMPASSIONATE COMMUNICATION' },
  { page: 73, title: 'Lost Love & the Drive Towards Wholeness' },
  { page: 75, title: 'When Your Past Shows Up in the Present' },
  { page: 78, title: 'Weekly Homework Partner Schedule' },
];

const WORKBOOK_CHAPTERS = [
  {
    number: 1,
    title: 'Communication that Blocks Compassion',
    items: [
      { page: 79, title: 'Chapter 1 Presentation View' },
      { page: 81, title: 'Our 4 Basic Choices of Response' },
      { page: 82, title: 'Four Basic Choices Exercise (p.82-86)' },
      { page: 87, title: 'Jackal Statements (OFNR Practice)' },
      { page: 88, title: 'Jackal Statements (Street Giraffe Practice)' },
      { page: 89, title: 'Using OFNR in Honesty and Empathy' },
      { page: 90, title: "The Four D's of Disconnection" },
      { page: 92, title: 'Chapter 1 Possible Answers' },
      { page: 93, title: 'Chapter 1 Homework' },
    ]
  },
  {
    number: 2,
    title: 'Observations',
    items: [
      { page: 94, title: 'Chapter 2 Presentation' },
      { page: 95, title: 'Exercise 1: Observation or Evaluation?' },
      { page: 97, title: 'Quiz Game & Practice' },
    ]
  },
  {
    number: 3,
    title: 'Feelings',
    items: [
      { page: 99, title: 'Chapter 3 Presentation' },
      { page: 100, title: 'Feelings when needs ARE satisfied' },
      { page: 101, title: 'Feelings when needs ARE NOT satisfied' },
      { page: 102, title: 'Exercise 2: Feeling or Thought?' },
      { page: 103, title: 'Non-Feeling Words' },
      { page: 104, title: 'Chapter 3 Possible Answers' },
      { page: 105, title: 'Alarm Feelings: Anger, Guilt, Shame, & Depression' },
      { page: 106, title: 'Acceptance, Clarity and Mourning' },
      { page: 107, title: 'Chapter 3 Homework' },
    ]
  },
  {
    number: 4,
    title: 'Needs',
    items: [
      { page: 108, title: 'Chapter 4 Introduction' },
      { page: 109, title: 'Exercise 2' },
      { page: 110, title: 'Exercise 3' },
      { page: 111, title: 'Information in Celebration' },
      { page: 112, title: 'Chapter 4 Possible Answers' },
      { page: 113, title: 'Chapter 4 Homework' },
    ]
  },
  {
    number: 5,
    title: 'Requests',
    items: [
      { page: 114, title: 'Chapter 5 Introduction' },
      { page: 115, title: 'Exercise 2' },
      { page: 116, title: 'Exercise 3' },
      { page: 117, title: 'Exercise 4' },
      { page: 118, title: '"Because I Care So Much" Cards' },
      { page: 119, title: 'What Keeps Me from Making a Request?' },
      { page: 120, title: 'COMMON FEARS AND BELIEFS' },
      { page: 121, title: '"Don\'t Criticize Me!" vs. Do-able requests' },
      { page: 123, title: 'When Every Request is a Demand' },
      { page: 125, title: "Let's Make Our Life More Wonderful Together" },
      { page: 126, title: '101 Ideas for Offering and Asking' },
      { page: 129, title: '101 Magic Words and Phrases' },
      { page: 133, title: 'Chapter 5 Possible Answers' },
      { page: 134, title: 'Chapter 5 Homework' },
    ]
  },
  {
    number: 6,
    title: 'Empathy',
    items: [
      { page: 135, title: 'Chapter 6 Introduction' },
      { page: 136, title: 'Exercise 2: Empathizing with Others' },
      { page: 137, title: 'Exercise 3' },
      { page: 138, title: 'Chapter 6 Possible Answers' },
      { page: 139, title: 'Guidelines for Giving Empathy' },
      { page: 141, title: 'EMPATHY is... Empathy is not...' },
      { page: 142, title: 'Empathy Without Words' },
      { page: 144, title: "The Power of Silent Empathy When Words Don't Work" },
      { page: 145, title: 'Self-Empathy Fundamentals' },
      { page: 147, title: 'Chapter 6 Homework' },
    ]
  },
  {
    number: 7,
    title: 'Working with Anger',
    items: [
      { page: 148, title: 'Chapter 7 Introduction' },
      { page: 150, title: 'Exercise 2' },
      { page: 151, title: 'Exercise 3' },
      { page: 152, title: 'From Anger to Transformation' },
      { page: 154, title: 'Chapter 7 Homework' },
    ]
  },
  {
    number: 8,
    title: 'Practical Applications of NVC',
    items: [
      { page: 155, title: '"Have To" → "Choose To" Exercise' },
      { page: 156, title: 'Translating self-judgment to self-empathy' },
      { page: 158, title: 'Translating judgments of others' },
      { page: 160, title: 'Expressing Gratitude & Appreciation' },
      { page: 162, title: 'How to say "no" in giraffe' },
      { page: 163, title: 'Exercise' },
      { page: 165, title: 'Responding Empathetically to a Jackal "No"' },
      { page: 167, title: 'Possible answers' },
      { page: 168, title: 'Mourning Process with Self Only' },
      { page: 170, title: 'NVC Mourning Process with Self and Others' },
      { page: 173, title: 'Receiving Compliments' },
      { page: 175, title: 'Sounding Natural' },
      { page: 177, title: 'NEEDS: Universal Needs vs. Strategies' },
      { page: 178, title: 'Street Giraffe' },
      { page: 180, title: 'Colloquial Options for speaking NVC' },
      { page: 182, title: 'Chapter 8 Homework' },
      { page: 183, title: '30 Daily Practice Ideas for Living Gratefully' },
      { page: 187, title: "NVC Basics Participant's Evaluation" },
    ]
  },
];

export default function EbookHighlightManager() {
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newHighlight, setNewHighlight] = useState({
    page: '',
    title: '',
    session_name: '',
    highlight_type: 'featured'
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedChapters, setExpandedChapters] = useState({});
  const [showReferences, setShowReferences] = useState(false);

  useEffect(() => {
    fetchHighlights();
  }, []);

  const fetchHighlights = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ebook/highlights/all`);
      if (response.ok) {
        const data = await response.json();
        setHighlights(data);
      }
    } catch (error) {
      console.error('Error fetching highlights:', error);
      toast.error('Failed to load highlights');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAdd = (pageInfo) => {
    setNewHighlight({
      ...newHighlight,
      page: pageInfo.page.toString(),
      title: pageInfo.title
    });
    setShowAddForm(true);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddHighlight = async () => {
    if (!newHighlight.page || !newHighlight.title) {
      toast.error('Please fill in page and title');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/ebook/highlights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newHighlight,
          page: parseInt(newHighlight.page)
        })
      });

      if (response.ok) {
        toast.success('Highlight added!');
        setNewHighlight({ page: '', title: '', session_name: '', highlight_type: 'featured' });
        setShowAddForm(false);
        fetchHighlights();
      } else {
        toast.error('Failed to add highlight');
      }
    } catch (error) {
      console.error('Error adding highlight:', error);
      toast.error('Failed to add highlight');
    }
  };

  const handleToggle = async (id) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ebook/highlights/toggle/${id}`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.active ? 'Highlight activated' : 'Highlight deactivated');
        fetchHighlights();
      }
    } catch (error) {
      console.error('Error toggling highlight:', error);
      toast.error('Failed to toggle highlight');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this highlight?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/ebook/highlights/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Highlight deleted');
        fetchHighlights();
      }
    } catch (error) {
      console.error('Error deleting highlight:', error);
      toast.error('Failed to delete highlight');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'featured': return <Star className="w-4 h-4 text-amber-500" />;
      case 'upcoming': return <BookOpen className="w-4 h-4 text-blue-500" />;
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return <Star className="w-4 h-4 text-amber-500" />;
    }
  };

  const getTypeBadgeClass = (type) => {
    switch (type) {
      case 'featured': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'upcoming': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const toggleChapter = (chapterNum) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chapterNum]: !prev[chapterNum]
    }));
  };

  // Check if a page is already highlighted
  const isPageHighlighted = (pageNum) => {
    return highlights.some(h => h.page === pageNum && h.active);
  };

  // Filter items by search term
  const filterItems = (items) => {
    if (!searchTerm) return items;
    return items.filter(item =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.page.toString().includes(searchTerm)
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ebook-highlight-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">eBook Highlights of the Week</h2>
          <p className="text-sm text-slate-500">Select pages from the table of contents to feature for students</p>
        </div>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="add-highlight-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Custom Highlight
        </Button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium text-emerald-800">New Highlight</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-600">Page Number</label>
                <Input
                  type="number"
                  value={newHighlight.page}
                  onChange={(e) => setNewHighlight({...newHighlight, page: e.target.value})}
                  placeholder="e.g., 87"
                  className="mt-1"
                  data-testid="highlight-page-input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-slate-600">Title</label>
                <Input
                  value={newHighlight.title}
                  onChange={(e) => setNewHighlight({...newHighlight, title: e.target.value})}
                  placeholder="e.g., Jackal to OFNR Practice"
                  className="mt-1"
                  data-testid="highlight-title-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Type</label>
                <Select
                  value={newHighlight.highlight_type}
                  onValueChange={(value) => setNewHighlight({...newHighlight, highlight_type: value})}
                >
                  <SelectTrigger className="mt-1" data-testid="highlight-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="featured">⭐ Featured This Week</SelectItem>
                    <SelectItem value="upcoming">📖 Coming Up Next</SelectItem>
                    <SelectItem value="completed">✅ We Covered This</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Session/Week Name (optional)</label>
              <Input
                value={newHighlight.session_name}
                onChange={(e) => setNewHighlight({...newHighlight, session_name: e.target.value})}
                placeholder="e.g., Week 3 - Empathy Deep Dive"
                className="mt-1"
                data-testid="highlight-session-input"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAddHighlight} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-highlight-btn">
                Add Highlight
              </Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Highlights */}
      <Card>
        <CardHeader className="py-3 border-b">
          <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-500" />
            Currently Featured ({highlights.filter(h => h.active).length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {highlights.filter(h => h.active).length === 0 ? (
            <div className="py-8 text-center text-slate-400">
              No pages featured yet. Select pages from the table of contents below.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {highlights.filter(h => h.active).map((highlight) => (
                <div key={highlight._id} className="p-4 flex items-center justify-between hover:bg-slate-50" data-testid={`active-highlight-${highlight.page}`}>
                  <div className="flex items-center gap-4">
                    <span className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold">
                      {highlight.page}
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">{highlight.title}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs border flex items-center gap-1 ${getTypeBadgeClass(highlight.highlight_type)}`}>
                          {getTypeIcon(highlight.highlight_type)}
                          <span className="capitalize">{highlight.highlight_type}</span>
                        </span>
                      </div>
                      {highlight.session_name && (
                        <p className="text-xs text-slate-500 mt-1">{highlight.session_name}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggle(highlight._id)}
                      className="border-slate-300"
                      title="Hide this highlight"
                    >
                      <EyeOff className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(highlight._id)}
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      title="Delete highlight"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search pages by title or number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="highlight-search-input"
        />
      </div>

      {/* Table of Contents - Chapters */}
      <Card>
        <CardHeader className="py-3 border-b">
          <CardTitle className="text-sm font-medium text-slate-600">
            Workbook Chapters - Click any page to feature it
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {WORKBOOK_CHAPTERS.map((chapter) => {
            const filteredItems = filterItems(chapter.items);
            if (searchTerm && filteredItems.length === 0) return null;
            
            return (
              <div key={chapter.number} className="border-b border-slate-100 last:border-b-0">
                <button
                  onClick={() => toggleChapter(chapter.number)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {expandedChapters[chapter.number] ? (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    )}
                    <span className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm">
                      {chapter.number}
                    </span>
                    <span className="font-medium text-slate-700">{chapter.title}</span>
                  </div>
                  <span className="text-xs text-slate-400">{chapter.items.length} pages</span>
                </button>
                
                {(expandedChapters[chapter.number] || searchTerm) && (
                  <div className="px-4 pb-3 pt-1 bg-slate-50/50">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {filteredItems.map((item) => {
                        const highlighted = isPageHighlighted(item.page);
                        return (
                          <button
                            key={item.page}
                            onClick={() => !highlighted && handleQuickAdd(item)}
                            disabled={highlighted}
                            className={`text-left px-3 py-2 rounded-lg border transition-all ${
                              highlighted
                                ? 'bg-emerald-100 border-emerald-300 cursor-default'
                                : 'bg-white border-slate-200 hover:border-emerald-400 hover:shadow-sm cursor-pointer'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${
                                highlighted ? 'bg-emerald-200 text-emerald-800' : 'bg-slate-100 text-slate-600'
                              }`}>
                                p.{item.page}
                              </span>
                              {highlighted && <Star className="w-3 h-3 text-emerald-600" />}
                            </div>
                            <p className={`text-sm mt-1 line-clamp-2 ${highlighted ? 'text-emerald-700' : 'text-slate-700'}`}>
                              {item.title}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Reference Materials Section */}
      <Card>
        <CardHeader className="py-3 border-b">
          <button
            onClick={() => setShowReferences(!showReferences)}
            className="w-full flex items-center justify-between"
          >
            <CardTitle className="text-sm font-medium text-slate-600">
              Reference Materials & Tools (p.1-78)
            </CardTitle>
            {showReferences ? (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-400" />
            )}
          </button>
        </CardHeader>
        {showReferences && (
          <CardContent className="p-4 bg-slate-50/50">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {filterItems(REFERENCE_MATERIALS).map((item) => {
                const highlighted = isPageHighlighted(item.page);
                return (
                  <button
                    key={item.page}
                    onClick={() => !highlighted && handleQuickAdd(item)}
                    disabled={highlighted}
                    className={`text-left px-3 py-2 rounded-lg border transition-all ${
                      highlighted
                        ? 'bg-emerald-100 border-emerald-300 cursor-default'
                        : 'bg-white border-slate-200 hover:border-emerald-400 hover:shadow-sm cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${
                        highlighted ? 'bg-emerald-200 text-emerald-800' : 'bg-slate-100 text-slate-600'
                      }`}>
                        p.{item.page}
                      </span>
                      {highlighted && <Star className="w-3 h-3 text-emerald-600" />}
                    </div>
                    <p className={`text-sm mt-1 line-clamp-2 ${highlighted ? 'text-emerald-700' : 'text-slate-700'}`}>
                      {item.title}
                    </p>
                  </button>
                );
              })}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Inactive Highlights */}
      {highlights.filter(h => !h.active).length > 0 && (
        <Card className="opacity-75">
          <CardHeader className="py-3 border-b">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <EyeOff className="w-4 h-4" />
              Previously Featured ({highlights.filter(h => !h.active).length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {highlights.filter(h => !h.active).map((highlight) => (
                <div key={highlight._id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <span className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 font-bold">
                      {highlight.page}
                    </span>
                    <div>
                      <span className="font-medium text-slate-500">{highlight.title}</span>
                      {highlight.session_name && (
                        <p className="text-xs text-slate-400 mt-1">{highlight.session_name}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggle(highlight._id)}
                      className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                      title="Re-activate highlight"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(highlight._id)}
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      title="Delete highlight"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
